import { query, Unsubscribe } from 'firebase/firestore';
import { useMemo } from 'react';
import { useShallow } from 'zustand/shallow';
import { useAuthContext } from '../contexts/auth.context';
import { DailyReading, DailyReadingCollection, DailyReadingConverter, FirestoreDailyReading } from '../dto/reading';
import { User, UserCollection } from '../dto/user';
import { UserReading, UserReadingCollection, UserReadingConverter } from '../dto/user-reading';
import { useDataStore } from '../stores/data.store';
import { getIsoDay } from '../util/util';
import { useFirebase } from './firebase.hook';

export interface ReadingInterface {
  loadReadings(user: User): Unsubscribe;
  resetReadings(): void;
  readingsLoading: boolean;
  completedReadings: UserReading[];
  dailyReading: DailyReading | undefined;
  hasReadingToDo: boolean;

  markReadingCompleted(id: string): Promise<void>;
}

export function useReading(): ReadingInterface {
  const { user } = useAuthContext();
  const { getDocumentConverted, setDocument, collection, querySubscribe, waitIfOnline } = useFirebase();

  const [storeDailyReading, setStoreDailyReading, storeCompletedReadings, setStoreCompletedReadings] = useDataStore(
    useShallow((s) => [s.dailyReading, s.setDailyReading, s.completedReadings, s.setCompletedReadings]),
  );

  let timeoutId: NodeJS.Timeout | undefined;

  // DB methods
  function loadReadings(user: User): Unsubscribe {
    loadDailyReading();

    const unsubscribeCompleted = querySubscribe<UserReading>(
      query(collection(`${UserCollection}/${user.id}/${UserReadingCollection}`).withConverter(UserReadingConverter)),
      setStoreCompletedReadings,
    );

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      unsubscribeCompleted();
    };
  }

  function resetReadings(): void {
    setStoreCompletedReadings(undefined);
  }

  // user methods
  async function markReadingCompleted(id: string): Promise<void> {
    if (!user || !storeDailyReading) return;

    const userReadingsPath = `${UserCollection}/${user.id}/${UserReadingCollection}`;
    const userReading = {
      completedAt: new Date(),
    };

    await waitIfOnline(setDocument(userReadingsPath, userReading, id));
  }

  // helper methods
  function loadDailyReading(): void {
    getDocumentConverted<FirestoreDailyReading, DailyReading>(
      DailyReadingCollection,
      getIsoDay(new Date()),
      DailyReadingConverter,
    ).then(setStoreDailyReading);

    scheduleNextLoad();
  }

  function scheduleNextLoad(): void {
    const now = new Date();

    const midnight = new Date(now);
    midnight.setDate(midnight.getDate() + 1);
    midnight.setHours(0, 0, 0, 0);

    timeoutId = setTimeout(() => {
      loadDailyReading();
    }, midnight.getTime() - now.getTime());
  }

  const completedReadingIds = useMemo(() => {
    return new Set((storeCompletedReadings ?? []).map((r: UserReading) => r.id));
  }, [storeCompletedReadings]);

  return useMemo(
    () => ({
      loadReadings,
      resetReadings,
      readingsLoading: user != null && !storeCompletedReadings,
      completedReadings: storeCompletedReadings ?? [],
      dailyReading: storeDailyReading,
      hasReadingToDo: !completedReadingIds.has(getIsoDay(new Date())),
      markReadingCompleted,
    }),
    [
      storeDailyReading,
      storeCompletedReadings,
      completedReadingIds,
      user,
      getDocumentConverted,
      setDocument,
      collection,
      querySubscribe,
      waitIfOnline,
    ],
  );
}
